import getSupportedEvents from "../getSupportedEvents"

const CHANGE_FROM_KEY = 'range-change-from'
const CHANGE_TO_KEY = 'range-change-to'

class RangeSlider extends HTMLElement {
  min: number = 0
  max: number = 100
  from: number = 0
  to: number = 0
  step: number = 1
  type: 'single' | 'double' = 'single'
  inner: HTMLElement = document.createElement('div')
  renderValue: HTMLElement = document.createElement('div')
  renderSign: HTMLElement = document.createElement('div')
  inputFrom: HTMLInputElement = document.createElement('input')
  inputTo: HTMLInputElement = document.createElement('input')
  line: HTMLElement = document.createElement('div')
  progress: HTMLElement = document.createElement('div')
  nextBtn: HTMLElement = document.createElement('div')
  prevBtn: HTMLElement = document.createElement('div')
  inited: boolean = false
  round: number = 4
  inputClassName: string = ''
  formatter = new Intl.NumberFormat()

  constructor() {
    super()
  }

  connectedCallback() {
    this.min = +(this.getAttribute('min') || 0)
    this.max = +(this.getAttribute('max') || 100)
    this.from = +(this.getAttribute('from') || this.min)
    this.to = +(this.getAttribute('to') || this.max)
    this.step = +(this.getAttribute('step') || 1)
    this.type = this.getAttribute('type') as 'single' | 'double' || 'single'
    this.round = +(this.getAttribute('round') || 4)


    this.inputClassName = this.getAttribute('input-class') || ''
    this.inited = false

    if (this.type == 'single') this.from = this.min
    if (this.from < this.min) this.from = this.min
    if (this.to > this.max) this.to = this.max

    // @ts-expect-error listener is correct
    this.inner.addEventListener(getSupportedEvents().start, this.handleStart.bind(this))

    if (this.inputFrom) {
      this.inputFrom.addEventListener('input', this.inputHandler)
      this.inputFrom.addEventListener('focus', this.inputHandler)
      this.inputFrom.addEventListener('change', (event) => this.changeHandler(event, 'from'))
      this.inputFrom.addEventListener('blur', (event) => this.changeHandler(event, 'from'))
    }

    if (this.inputTo) {
      this.inputTo.addEventListener('input', this.inputHandler)
      this.inputTo.addEventListener('focus', this.inputHandler)
      this.inputTo.addEventListener('change', (event) => this.changeHandler(event, 'to'))
      this.inputTo.addEventListener('blur', (event) => this.changeHandler(event, 'to'))
    }

    this.addElements()
    this.inited = true

    this.render()
  }

  changeHandler(event: Event, key: "from" | "to") {
    if (!event.target) return

    const target = event.target as HTMLInputElement
    target.value = target.value.replace(/\D/g, '')
    const defaultValues = {
      from: this.min,
      to: this.max
    }
    this.setFromTo(+(target.value === '' ? defaultValues[key] : target.value), key)

    this.render()
  }

  inputHandler(event: Event) {
    if (!event.target) return

    const target = event.target as HTMLInputElement
    target.value = target.value.replace(/\D/g, '')
  }

  addElements() {
    const inputs = document.createElement('div')
    inputs.classList.add('range-inputs')
    this.inputFrom.classList.add(...this.inputClassName.split(' '))
    this.inputFrom.classList.add('range-input')
    this.inputTo.classList.add(...this.inputClassName.split(' '))
    this.inputTo.classList.add('range-input')
    if (this.type == 'double') {
      inputs.appendChild(this.inputFrom)
    }
    inputs.appendChild(this.inputTo)
    this.inputFrom.placeholder = this.min.toString()
    this.inputTo.placeholder = this.max.toString()
    this.appendChild(inputs)
    this.inner.classList.add('range-inner')

    this.line.classList.add('range-line')
    this.inner.appendChild(this.line)

    this.progress.classList.add('range-progress')
    this.inner.appendChild(this.progress)

    this.nextBtn.classList.add('range-btn')
    this.inner.appendChild(this.nextBtn)

    if (this.type == 'double') {
      this.prevBtn.classList.add('range-btn')
      this.inner.appendChild(this.prevBtn)
    }

    this.appendChild(this.inner)
  }

  roundToStep(value: number): number {
    if (this.step <= 0) return value
    return Math.round((value - this.min) / this.step) * this.step + this.min
  }

  dispatchFromChange() {
    const eventFromChange = new CustomEvent(CHANGE_FROM_KEY, {
      bubbles: true,
      detail: {
        value: this.from
      }
    });

    this.dispatchEvent(eventFromChange)
  }

  dispatchToChange() {
    const eventToChange = new CustomEvent(CHANGE_TO_KEY, {
      bubbles: true,
      detail: {
        value: this.to
      }
    });

    this.dispatchEvent(eventToChange)
  }

  setFromTo(value: number, key?: 'from' | 'to'): 'from' | 'to' {
    // Округляем значение до ближайшего step
    value = this.roundToStep(value)
    const oldFrom = this.from
    const oldTo = this.to

    let result: 'from' | 'to' = 'from'
    if (key) {
      this[key] = value
      result = key
    } else if (value >= +this.to || this.type == 'single') {
      this.to = value
      result = 'to'
    } else if (value <= +this.from) {
      this.from = value
      result = 'from'
    } else {
      if ((+this.to - value) < -(+this.from - value)) {
        this.to = value
        result = 'to'
      } else {
        this.from = value
        result = 'from'
      }
    }

    if (this.to <= this.from && result == 'from') this.to = this.from
    if (this.to <= this.from && result == 'to') this.from = this.to
    if (+this.to > this.max) this.to = this.max
    if (+this.to < this.min) this.to = this.min
    if (+this.from < this.min) this.from = this.min
    if (+this.from > this.max) this.from = this.max

    let fromValue = this.roundToStep(this.from)
    let toValue = this.roundToStep(this.to)

    if (this.round) {
      fromValue = parseFloat((+fromValue).toFixed(this.round))
      toValue = parseFloat((+toValue).toFixed(this.round))
    }

    this.from = fromValue
    this.to = toValue

    if (oldFrom !== this.from) {
      this.dispatchFromChange()
    }

    if (oldTo !== this.to) {
      this.dispatchToChange()
    }

    return result
  }

  render() {
    const inner = this.inner
    if (!inner) {
      return new Error("range inner is undefined");
    }

    const innerWidth = inner.getBoundingClientRect().width
    const left = (+this.from - this.min) / (this.max - this.min) * innerWidth
    const right = (+this.to - this.min) / (this.max - this.min) * innerWidth
    this.nextBtn.style.left = right + 'px'
    this.progress.style.left = left + 'px'
    this.progress.style.width = right - left + 'px'

    if (this.type == 'double') {
      this.prevBtn.style.left = left + 'px'
    }

    if (this.type == 'double' && this.renderValue) this.renderValue.innerHTML = Math.floor(+this.from) + ' — ' + Math.floor(+this.to)
    if (this.type == 'single' && this.renderValue) this.renderValue.innerHTML = Math.floor(+this.to).toString()

    if (this.renderSign) {
      const sign = this.renderSign
      const signWidth = sign.getBoundingClientRect().width
      let signLeft = 0

      if (this.type == 'single') {
        signLeft = right - signWidth / 2

      } else if (this.type == 'double') {
        signLeft = left + (right - left) / 2 - signWidth / 2

      }

      if (signLeft < 0) signLeft = 0
      if ((signLeft + signWidth) > innerWidth) signLeft = innerWidth - signWidth
      sign.style.left = signLeft + 'px'
    }

    if (this.inputFrom) {
      this.inputFrom.value = this.formatter.format(this.from).replace(/\D/g, ' ');
      if (this.from == this.min) {
        this.inputFrom.value = ''
      }
    }

    if (this.inputTo) {
      this.inputTo.value = this.formatter.format(this.to).replace(/\D/g, ' ');

      if (this.to == this.max) {
        this.inputTo.value = ''
      }
    }
  }


  handleStart = (e: PointerEvent | TouchEvent) => {
    const event = 'changedTouches' in e ? e.changedTouches[0] : e

    const left = this.inner.getBoundingClientRect().left
    const currentValue = ((event.clientX - left) / this.inner.getBoundingClientRect().width) * (this.max - this.min) + this.min
    const key = this.setFromTo(currentValue)
    this.render()

    document.body.style.userSelect = 'none'


    let timerFlag = true

    const moveHandler = (e: PointerEvent | TouchEvent) => {
      if (!timerFlag) return
      timerFlag = false

      setTimeout(() => timerFlag = true, 1000 / 60)

      const event = 'changedTouches' in e ? e.changedTouches[0] : e
      const left = this.inner.getBoundingClientRect().left
      const moveValue = ((event.clientX - left) / this.inner.getBoundingClientRect().width) * (this.max - this.min) + this.min

      this.setFromTo(moveValue, key)
      this.render()


      this.progress.style.transition = '0s'
      this.nextBtn.style.transition = '0s'
      this.prevBtn.style.transition = '0s'
      this.renderSign.style.transition = '0s'

    }

    // @ts-expect-error listener is correct
    document.addEventListener(getSupportedEvents().move, moveHandler)

    const cancelHandler = () => {
      document.body.style.userSelect = ''

      this.progress.style.transition = ''
      this.nextBtn.style.transition = ''
      this.prevBtn.style.transition = ''
      this.renderSign.style.transition = ''

      // @ts-expect-error listener is correct
      document.removeEventListener(getSupportedEvents().move, moveHandler)
      document.removeEventListener(getSupportedEvents().end, cancelHandler)
      document.removeEventListener(getSupportedEvents().cancel, cancelHandler)
    }

    document.addEventListener(getSupportedEvents().end, cancelHandler)
    document.addEventListener(getSupportedEvents().cancel, cancelHandler)
  }
}

function register() {
  customElements.define('range-slider', RangeSlider)
}

export default { register }